import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PartCategory } from '@bhops/shared';
import { partsApi } from '@/api/parts';
import { suppliersApi } from '@/api/suppliers';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Input, Select } from '@/components/Form';
import { useToast } from '@/components/Toast';

// docs/plan/v0.4.0-parts-solution-defect-lot/plan.md §5.2 — 카탈로그 등록.

const CATEGORY_OPTIONS: Array<{ value: PartCategory; label: string }> = [
  { value: PartCategory.GRILLER, label: '그릴러' },
  { value: PartCategory.LOADER, label: '로더' },
  { value: PartCategory.GAS_VALVE, label: '가스 밸브' },
  { value: PartCategory.FIRMWARE_BOARD, label: '펌웨어 보드' },
  { value: PartCategory.SENSOR_SUITE, label: '센서 모듈' },
  { value: PartCategory.POWER_UNIT, label: '전원 유닛' },
  { value: PartCategory.CONSUMABLE, label: '소모품' },
  { value: PartCategory.ACCESSORY, label: '액세서리' },
];

export interface PartCatalogCreateModalProps {
  open: boolean;
  onClose: () => void;
}

export function PartCatalogCreateModal({ open, onClose }: PartCatalogCreateModalProps) {
  const toast = useToast();
  const qc = useQueryClient();
  const [sku, setSku] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [category, setCategory] = useState<PartCategory>(PartCategory.GRILLER);
  const [unitPriceKrw, setUnitPriceKrw] = useState<number | ''>('');
  const [warrantyMonths, setWarrantyMonths] = useState<number | ''>('');
  // v0.5.2 — 재고 자동화.
  const [minStockLevel, setMinStockLevel] = useState<number | ''>('');
  const [reorderQty, setReorderQty] = useState<number | ''>('');
  const [defaultSupplierId, setDefaultSupplierId] = useState('');
  const [error, setError] = useState<string | null>(null);

  // v0.5.2 — supplier picker.
  const suppliersQ = useQuery({
    queryKey: ['suppliers', { active: true }],
    queryFn: () => suppliersApi.list({ active: true }),
    staleTime: 60_000,
  });

  const createMx = useMutation({
    mutationFn: () =>
      partsApi.createCatalog({
        sku: sku.trim(),
        displayName: displayName.trim(),
        category,
        unitPriceKrw: typeof unitPriceKrw === 'number' ? unitPriceKrw : undefined,
        warrantyMonths: typeof warrantyMonths === 'number' ? warrantyMonths : undefined,
        minStockLevel: typeof minStockLevel === 'number' ? minStockLevel : undefined,
        reorderQty: typeof reorderQty === 'number' ? reorderQty : undefined,
        defaultSupplierId: defaultSupplierId || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parts', 'catalog'] });
      toast.success('부품 카탈로그를 등록했습니다.');
      handleClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function handleClose(): void {
    if (createMx.isPending) return;
    setSku('');
    setDisplayName('');
    setCategory(PartCategory.GRILLER);
    setUnitPriceKrw('');
    setWarrantyMonths('');
    setMinStockLevel('');
    setReorderQty('');
    setDefaultSupplierId('');
    setError(null);
    onClose();
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setError(null);
    if (!sku.trim()) return setError('SKU를 입력해 주세요.');
    if (sku.trim().length < 2) return setError('SKU는 2자 이상이어야 합니다.');
    if (!displayName.trim()) return setError('이름을 입력해 주세요.');
    createMx.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="부품 카탈로그 등록"
      width={500}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={createMx.isPending}
          >
            취소
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={createMx.isPending}
            data-testid="catalog-create-submit"
          >
            {createMx.isPending ? '저장 중…' : '등록'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {error && <FormError>{error}</FormError>}

        <Field
          label="SKU"
          htmlFor="sku"
          required
          hint="공급사나 사내에서 붙인 식별 코드입니다. 예: GRL-LPG-V2-A1"
        >
          <Input
            id="sku"
            value={sku}
            onChange={(e) => setSku(e.target.value.toUpperCase())}
            placeholder="GRL-LPG-V2-A1"
            data-testid="catalog-sku-input"
          />
        </Field>

        <Field label="이름" htmlFor="display-name" required>
          <Input
            id="display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="예: LPG 그릴러 v2.1"
            data-testid="catalog-name-input"
          />
        </Field>

        <Field label="카테고리" htmlFor="category" required>
          <Select
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value as PartCategory)}
            data-testid="catalog-category-select"
          >
            {CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="단가 ₩ (선택)" htmlFor="unit-price">
          <Input
            id="unit-price"
            type="number"
            min={0}
            step={1000}
            value={unitPriceKrw}
            onChange={(e) => {
              const v = e.target.value;
              setUnitPriceKrw(v === '' ? '' : Number.parseFloat(v));
            }}
            placeholder="350000"
          />
        </Field>

        <Field
          label="보증 기간 (개월, 선택)"
          htmlFor="warranty"
          hint="로봇에 장착한 부품의 보증 만료일을 계산할 때 씁니다."
        >
          <Input
            id="warranty"
            type="number"
            min={1}
            max={120}
            value={warrantyMonths}
            onChange={(e) => {
              const v = e.target.value;
              setWarrantyMonths(v === '' ? '' : Number.parseInt(v, 10));
            }}
            placeholder="12"
          />
        </Field>

        {/* v0.5.2 — 재고 자동화 섹션 */}
        <div
          style={{
            padding: '8px 0 4px',
            borderTop: '1px dashed var(--border-subtle)',
            marginTop: 8,
          }}
        >
          <div
            className="kuun-label"
            style={{
              fontSize: 11,
              color: 'var(--fg-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              fontWeight: 600,
            }}
          >
            재고 자동화 (선택)
          </div>
          <div
            className="kuun-caption"
            style={{ color: 'var(--fg-muted)', fontSize: 11, marginTop: 2 }}
          >
            최소 재고를 정해 두면 매일 오전 9시 30분에 재고를 확인해 부족할 때 알려 드립니다.
          </div>
        </div>

        <Field
          label="최소 재고 (선택)"
          htmlFor="min-stock"
          hint="비워 두면 재고를 추적하지 않습니다. 가용 재고가 이 값 이하로 떨어지면 Slack으로 알립니다."
        >
          <Input
            id="min-stock"
            type="number"
            min={0}
            value={minStockLevel}
            onChange={(e) => {
              const v = e.target.value;
              setMinStockLevel(v === '' ? '' : Number.parseInt(v, 10));
            }}
            placeholder="10"
            data-testid="catalog-min-stock-input"
          />
        </Field>

        <Field
          label="권장 발주량 (선택)"
          htmlFor="reorder-qty"
          hint="발주할 때 추천하는 수량입니다. Slack 알림에도 표시됩니다."
        >
          <Input
            id="reorder-qty"
            type="number"
            min={1}
            value={reorderQty}
            onChange={(e) => {
              const v = e.target.value;
              setReorderQty(v === '' ? '' : Number.parseInt(v, 10));
            }}
            placeholder="20"
          />
        </Field>

        <Field
          label="기본 공급사 (선택)"
          htmlFor="default-supplier"
          hint="자동 발주 때 주문할 공급사입니다. 자동 발주는 준비 중이고, 지금은 Slack 알림에 표시됩니다."
        >
          <Select
            id="default-supplier"
            value={defaultSupplierId}
            onChange={(e) => setDefaultSupplierId(e.target.value)}
            disabled={suppliersQ.isLoading}
          >
            <option value="">— 선택 안 함 —</option>
            {(suppliersQ.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} · {s.displayName}
              </option>
            ))}
          </Select>
        </Field>
      </form>
    </Modal>
  );
}
